import React, { useState } from "react";
import { baseUrl_PB } from "../../BaseUrl";
import "./document-processing.css";

const IndDocumentProcessing = () => {
  const [step, setStep] = useState(1);
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]); // one entry per file
  const [storeResults, setStoreResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 1: Handle multiple file selection
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (!selectedFiles.length) return;

    setFiles(selectedFiles);
    setError("");
  };
  function updateProgressUI(fileName, progress, status) {
    const el = document.getElementById(`progress-${fileName}`);
    if (el) {
      el.textContent = `${progress}% - ${status}`;
      el.style.width = `${progress}%`;
    } else {
      console.log(`${fileName}: ${progress}%`);
    }
  }
  // Step 1 -> Step 2: Upload & process each file
  const handleNextProcess = async () => {
    if (!files.length) {
      setError("Please select at least one file to upload.");
      return;
    }

    setLoading(true);
    setError("");
    setResults([]);

    try {
      const allResults = [];

      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);

        // Start file upload
        const response = await fetch(`${baseUrl_PB}/uploadmarkdownchunkdoc`, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) throw new Error(`Error processing file: ${file.name}`);

        // Get streaming reader
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let lastProgress = 0;

        // Process chunks of streamed data
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop(); // Keep incomplete message

          for (const part of parts) {
            if (part.startsWith("data:")) {
              const json = part.slice(5).trim();
              try {
                const event = JSON.parse(json);

                // Show progress update per file
                if (event.progress !== undefined) {
                  lastProgress = event.progress;
                  console.log(`File ${file.name}: ${event.progress}% - ${event.status}`);

                  // Update UI (optional)
                  updateProgressUI(file.name, event.progress, event.status);
                }

                // When done, store result
                if (event.progress === 100 && event.result) {
                  allResults.push({ fileName: file.name, data: event.result });
                }
              } catch (e) {
                console.warn("Invalid SSE chunk:", part);
              }
            }
          }
        }

        console.log(`✅ Completed ${file.name} (${lastProgress}%)`);
      }


      setResults(allResults);
      setStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 2 -> Step 3: Store processed data for each file
  const handleNextStore = async () => {
    if (!results.length) return;

    setLoading(true);
    setError("");

    try {
      const allStored = [];

      for (const result of results) {
        const response = await fetch(`${baseUrl_PB}/upsertmarkdownandchunkeddoctovdb`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(result.data),
        });

        if (!response.ok) throw new Error(`Error storing result for ${result.fileName}`);

        const data = await response.json();
        allStored.push({ fileName: result.fileName, data });
      }

      setStoreResults(allStored);
      setStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset everything
  const handleFinish = () => {
    setStep(1);
    setFiles([]);
    setResults([]);
    setStoreResults([]);
    setError("");
  };

  return (
    <div className="workflow-container">
      {loading && <div className="loading">Processing...</div>}
      {error && <div className="error">{error}</div>}

      {step === 1 && (
        <div className="step step-upload">
          <h2>Step 1: Upload Documents</h2>
          <input
            type="file"
            accept=".pdf,.doc,.docx,image/*,.ppt,.pptx,.txt,.xls,.xlsx,.csv"
            multiple
            onChange={handleFileChange}
          />
          {files.length > 0 && (
            <ul>
              {files.map(file => (
                <div key={file.name} className="progress-row">
                  <span>{file.name}</span>
                  <div className="progress-bar">
                    <div id={`progress-${file.name}`} className="progress-fill"></div>
                  </div>
                </div>
              ))}
            </ul>
          )}
          <button onClick={handleNextProcess} disabled={!files.length || loading}>
            Next
          </button>
        </div>
      )}

      {step === 2 && results.length > 0 && (
        <div className="step step-process">
          <h2>Step 2: Review and Save Results</h2>
          {results.map((r, i) => (
            <div key={i} className="result-block">
              <h4>{r.fileName}</h4>
              <pre className="result-output">{JSON.stringify(r.data, null, 2)}</pre>
            </div>
          ))}
          <button onClick={handleNextStore} disabled={loading}>Save All</button>
        </div>
      )}

      {step === 3 && storeResults.length > 0 && (
        <div className="step step-store">
          <h2>Step 3: Saved Successfully</h2>
          <ul>
            {storeResults.map((r, i) => (
              <li key={i}>
                <strong>{r.fileName}</strong> — saved into the vector space.
              </li>
            ))}
          </ul>
          <button onClick={handleFinish}>Start Again</button>
        </div>
      )}
    </div>
  );
};

export default IndDocumentProcessing;
